import { BarChart } from '@mantine/charts';
import type { TFunction } from 'i18next';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { FormAnalyticsAggregateResponse, FormAnalyticsResponse } from '@/api';
import { Fact, FactGrid } from '@/components/Ui/Fact';
import { Section } from '@/components/Ui/Section';
import { integerTicks } from '@/lib/chart-ticks';
import { formatNumber, formatPercent } from '@/lib/money';
import { shortDate } from '@/lib/relative-time';
import { useRustyState } from '@/state';
import { chartOther, chartSeries } from '@/theme';
import { formatDay, formatRange, lastDays } from './days';
import classes from './FormMessages.module.css';

const DAYS = 30;

interface DayBarsProps {
  title: string;
  days: string[];
  values: number[];
  color: string;
  readout: (day: string, count: number) => string;
  range: string;
}

function DayBars({ title, days, values, color, readout, range }: DayBarsProps) {
  const { t, i18n } = useTranslation();
  const [hover, setHover] = useState<number | null>(null);
  const data = days.map((day, i) => ({
    day: shortDate(day, i18n.language),
    value: values[i],
  }));
  const total = values.reduce((a, b) => a + b, 0);
  const max = Math.max(0, ...values);
  const ticks = integerTicks(max);
  const shown = hover ?? days.length - 1;

  return (
    <figure className={classes.figure}>
      <figcaption className={classes.caption}>
        <strong>{title}</strong>
        <span className={classes.rh} aria-live="polite">
          {readout(formatDay(days[shown], i18n.language), values[shown] ?? 0)}
        </span>
      </figcaption>
      <div role="img" aria-label={t('formMessages.chartAria', { title, range, total, max })}>
        <BarChart
          h={150}
          data={data}
          dataKey="day"
          series={[{ name: 'value', color, label: title }]}
          withTooltip={false}
          gridAxis="y"
          tickLine="none"
          yAxisProps={{
            allowDecimals: false,
            interval: 0,
            width: 32,
            ticks,
            domain: [0, ticks[ticks.length - 1]],
          }}
          xAxisProps={{ interval: 'preserveStartEnd', minTickGap: 40 }}
          barChartProps={{
            onMouseMove: (state) => {
              const idx = Number(state?.activeTooltipIndex);
              setHover(Number.isInteger(idx) ? idx : null);
            },
            onMouseLeave: () => setHover(null),
          }}
          barProps={{ radius: [3, 3, 0, 0] }}
        />
      </div>
    </figure>
  );
}

function formatDuration(seconds: number, t: TFunction) {
  const total = Math.round(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  return m > 0 ? t('formMessages.minutesSeconds', { m, s }) : t('formMessages.seconds', { s });
}

export function FormAnalytics({ formId }: { formId: string }) {
  const { t, i18n } = useTranslation();
  const [series, setSeries] = useState<FormAnalyticsResponse | null | undefined>(undefined);
  const [aggregate, setAggregate] = useState<FormAnalyticsAggregateResponse | null | undefined>(
    undefined
  );

  useEffect(() => {
    let live = true;
    const { api } = useRustyState.getState();
    api
      .getMessageCountByDay({ form_ids: [formId], data_type: 'combined', days: DAYS })
      .then((res) => live && setSeries(res as FormAnalyticsResponse))
      .catch(() => live && setSeries(null));
    api
      .getAnalyticsAggregate({ form_ids: [formId] })
      .then((res) => live && setAggregate(res))
      .catch(() => live && setAggregate(null));
    return () => {
      live = false;
    };
  }, [formId]);

  const days = useMemo(() => lastDays(DAYS), []);
  const range = formatRange(days[0], days[days.length - 1], i18n.language);

  const byDay = useMemo(() => {
    const map = new Map((series?.data ?? []).map((row) => [row.date.slice(0, 10), row]));
    return {
      views: days.map((d) => map.get(d)?.view_count ?? 0),
      messages: days.map((d) => map.get(d)?.message_count ?? 0),
    };
  }, [series, days]);

  const loading = series === undefined;
  const views = series?.view_count ?? 0;
  const messages = series?.message_count ?? 0;
  const dash = '–';
  const fmt = (n: number) => formatNumber(n, i18n.language);
  const avgSeconds = aggregate?.average_time_to_submit_message?.average_time_seconds ?? 0;

  const countries = useMemo(() => {
    const rows = [...(aggregate?.top_countries ?? [])].sort((a, b) => b.count - a.count);
    const total = rows.reduce((a, r) => a + r.count, 0);
    if (total === 0) return [];
    const top = rows.slice(0, 3).map((r) => ({
      name: r.country === 'Unknown' ? t('formMessages.unknownCountry') : r.country,
      count: r.count,
      color: chartSeries[0] as string,
    }));
    const rest = rows.slice(3).reduce((a, r) => a + r.count, 0);
    if (rest > 0) top.push({ name: t('formMessages.other'), count: rest, color: chartOther });
    return top.map((r) => ({ ...r, pct: Math.round((r.count / total) * 100) }));
  }, [aggregate, t]);

  return (
    <Section title={t('formMessages.last30')} subtitle={t('formMessages.rangeNote', { range })}>
      <div className={classes.facts}>
        <FactGrid>
          <Fact
            label={t('formMessages.views')}
            value={loading ? '…' : series ? fmt(views) : dash}
            help={series === null ? t('formMessages.notAvailable') : undefined}
          />
          <Fact
            label={t('formMessages.messages')}
            value={loading ? '…' : series ? fmt(messages) : dash}
          />
          <Fact
            label={t('formMessages.perView')}
            value={
              loading
                ? '…'
                : views > 0
                  ? formatPercent(Math.min((messages / views) * 100, 100), i18n.language, 1)
                  : dash
            }
            help={series && views === 0 ? t('formMessages.perViewNone') : undefined}
          />
          <Fact
            label={t('formMessages.averageTime')}
            value={
              aggregate === undefined ? '…' : avgSeconds > 0 ? formatDuration(avgSeconds, t) : dash
            }
            help={t('formMessages.averageTimeHelp')}
          />
        </FactGrid>
      </div>
      {series && (
        <div className={classes.charts}>
          <DayBars
            title={t('formMessages.viewsPerDay')}
            days={days}
            values={byDay.views}
            color={chartSeries[0]}
            range={range}
            readout={(day, count) => t('formMessages.viewsReadout', { day, count })}
          />
          <DayBars
            title={t('formMessages.messagesPerDay')}
            days={days}
            values={byDay.messages}
            color={chartSeries[1]}
            range={range}
            readout={(day, count) => t('formMessages.messagesReadout', { day, count })}
          />
        </div>
      )}
      <div className={classes.countries}>
        <strong>{t('formMessages.countries')}</strong>
        {aggregate === undefined ? (
          <span className={classes.rh}>…</span>
        ) : countries.length === 0 ? (
          <span className={classes.rh}>
            {aggregate === null ? t('formMessages.notAvailable') : t('formMessages.countriesEmpty')}
          </span>
        ) : (
          countries.map((c) => (
            <div key={c.name} className={classes.countryRow}>
              <span className={classes.countryName}>{c.name}</span>
              <span className={classes.countryTrack}>
                <span
                  className={classes.countryFill}
                  style={{ width: `${c.pct}%`, background: c.color }}
                />
              </span>
              <span className={`${classes.num} ${classes.rh}`}>
                {formatPercent(c.pct, i18n.language)}
              </span>
            </div>
          ))
        )}
      </div>
    </Section>
  );
}
